import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelectLib from "react-select/async";
import { Label } from "reactstrap";

export const AsyncSelect = ({
  name,
  control,
  label,
  loadOptions,
  isMulti = false,
  defaultValue = [],
  rules = {},
  placeholder = "",
  styles = {},
  getOptionValue = (option) => option.value,
  getOptionLabel = (option) => option.label,
  cacheOptions = false,
  defaultOptions = false,
  isLocal = false,
  disabledMarginBottom,
  ...restProps
}) => {
  const [focusLoaded, setFocusLoaded] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [themeVars, setThemeVars] = useState({});

  const isRequired = !!rules?.required; // ✅ Check if required

  // 🌓 Track theme variables and update dynamically
  useEffect(() => {
    const updateThemeVars = () => {
      const root = document.documentElement;
      setThemeVars({
        inputBg: getComputedStyle(root).getPropertyValue('--vz-input-bg-custom').trim(),
        bodyColor: getComputedStyle(root).getPropertyValue('--vz-body-color').trim(),
        borderColor: getComputedStyle(root).getPropertyValue('--vz-input-border-custom').trim(),
        tertiaryBg: getComputedStyle(root).getPropertyValue('--vz-tertiary-bg').trim(),
        invalidColor: getComputedStyle(root).getPropertyValue('--vz-form-invalid-color').trim(),
        primaryColor: getComputedStyle(root).getPropertyValue('--vz-primary').trim(),
        whiteColor: getComputedStyle(root).getPropertyValue('--vz-white').trim(),
        dangerColor: getComputedStyle(root).getPropertyValue('--vz-danger').trim(),
      });
    };

    updateThemeVars();
    const observer = new MutationObserver(updateThemeVars);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme", "class"],
    });
    return () => observer.disconnect();
  }, []);

  const wrappedLoadOptions = useCallback(
    async (inputValue) => {
      if (isLocal && dynamicOptions.length) {
        return dynamicOptions.filter((option) =>
          getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
        );
      } else {
        try {
          const options = await loadOptions(inputValue);
          if (!focusLoaded) {
            setDynamicOptions(options);
            setFocusLoaded(true);
          }
          return options;
        } catch (err) {
          console.warn("loadOptions error:", err);
          return [];
        }
      }
    },
    [isLocal, dynamicOptions, getOptionLabel, loadOptions, focusLoaded]
  );

  const handleFocus = useCallback(async () => {
    if (!focusLoaded && typeof loadOptions === "function") {
      setLocalLoading(true);
      try {
        const options = await loadOptions("");
        setDynamicOptions(options);
        setFocusLoaded(true);
      } catch (err) {
        console.warn("err:", err);
      } finally {
        setLocalLoading(false);
      }
    }
  }, [focusLoaded, loadOptions]);

  return (
    <div className={disabledMarginBottom ? "mb-0" : "mb-3"}>
      <style>
        {`
          .required-asterisk {
            color: red;
            margin-left: 4px;
          }
        `}
      </style>

      {label && (
        <Label htmlFor={name} className="form-label">
          {label}
          {isRequired && <span className="required-asterisk">*</span>}
        </Label>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          const hasError = !!error;
          const isDisabled = restProps.isDisabled || restProps.disabled;

          const customStyles = {
            control: (provided, state) => ({
              ...provided,
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: hasError
                ? themeVars.invalidColor
                : themeVars.borderColor,
              boxShadow: state.isFocused
                ? hasError
                  ? `0 0 0 1px ${themeVars.invalidColor}`
                  : "0 0 0 1px #2684FF"
                : "none",
              "&:hover": {
                borderColor: hasError
                  ? themeVars.invalidColor
                  : "#2684FF",
                cursor: "pointer",
              },
              minHeight: "38px",
              backgroundColor: isDisabled
                ? themeVars.tertiaryBg
                : themeVars.inputBg,
              color: themeVars.bodyColor,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 1 : undefined,
            }),
            menu: (provided) => ({
              ...provided,
              backgroundColor: themeVars.inputBg,
              color: themeVars.bodyColor,
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected
                ? themeVars.primaryColor
                : state.isFocused
                ? `${themeVars.primaryColor}22`
                : themeVars.inputBg,
              color: state.isSelected
                ? themeVars.whiteColor
                : themeVars.bodyColor,
              cursor: "pointer",
            }),
            input: (provided) => ({
              ...provided,
              color: themeVars.bodyColor,
            }),
            singleValue: (provided) => ({
              ...provided,
              color: themeVars.bodyColor,
            }),
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: themeVars.primaryColor,
              color: themeVars.whiteColor,
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: themeVars.whiteColor,
            }),
            multiValueRemove: (provided) => ({
              ...provided,
              color: themeVars.whiteColor,
              ":hover": {
                backgroundColor: themeVars.dangerColor,
                color: themeVars.whiteColor,
                cursor: "pointer",
              },
            }),
            loadingIndicator: (provided) => ({
              ...provided,
              color: themeVars.primaryColor,
            }),
            ...styles,
          };

          return (
            <>
              <AsyncSelectLib
                id={name}
                isMulti={isMulti}
                loadOptions={wrappedLoadOptions}
                placeholder={placeholder}
                getOptionValue={getOptionValue}
                getOptionLabel={getOptionLabel}
                styles={customStyles}
                cacheOptions={cacheOptions}
                defaultOptions={
                  dynamicOptions.length ? dynamicOptions : defaultOptions
                }
                onFocus={handleFocus}
                onChange={(selected) => field.onChange(selected)}
                value={field.value}
                filterOption={() => true}
                isLoading={localLoading}
                loadingMessage={() => (
                  <div style={{ color: themeVars.bodyColor, padding: "5px 10px" }}>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      style={{
                        color: themeVars.primaryColor,
                        verticalAlign: "middle",
                      }}
                    ></span>
                    Loading...
                  </div>
                )}
                {...field}
                {...restProps}
              />
              {hasError && (
                <div className="invalid-feedback d-block">{error.message}</div>
              )}
            </>
          );
        }}
      />
    </div>
  );
};
